import moment from "moment"
import { getBatchMomentOrTimeOnly, isBatchNewerThanPricing } from "helpers/intraday"

export interface LatestBatchCandidate {
  id: string
  asOf: string
  date?: string | null
}

// currentAsOf is a full ISO instant for a live-tick click but time-only for a batch click.
// currentBatchDate anchors the batch-click case to its own calendar day — the pinned batch can be
// a genuinely prior-day snapshot (open trades blocking auto-refresh), not always today's.
// intradayPricingAsOf is optional so callers with no live-pricing signal keep the old behavior.
export function resolveHasNewerBatch(
  latestBatch: LatestBatchCandidate | null | undefined,
  currentBatchId: string | null,
  currentAsOf: string | null,
  currentBatchDate?: string | null,
  intradayPricingAsOf?: string | null,
): boolean {
  const currentAsOfIso = currentAsOf
    ? (currentBatchId ? getBatchMomentOrTimeOnly({ date: currentBatchDate, asOf: currentAsOf })?.toISOString() ?? null : currentAsOf)
    : null

  const hasNewerCommittedBatch = Boolean(latestBatch?.id)
    && latestBatch?.id !== currentBatchId
    && isBatchNewerThanPricing(latestBatch, currentAsOfIso)

  // Live pricing keeps ticking even once the latest committed batch is what's currently shown —
  // without this, pinning to the newest batch stranded the panel with no way to roll forward.
  const hasNewerLivePricing = Boolean(intradayPricingAsOf)
    && Boolean(currentAsOfIso)
    && moment(intradayPricingAsOf).isAfter(moment(currentAsOfIso))

  return hasNewerCommittedBatch || hasNewerLivePricing
}

export interface RefreshTarget {
  batchId: string | null
  asOf: string | null
  date: string | null
}

// "Refresh" must land on whichever is actually the latest snapshot right now - if live pricing
// has since moved past even the latest committed batch, that's live pricing, not the batch.
// preferBatch overrides that for views with no live-pricing data of their own (performance
// contributors only ever populate from a batch) - clearing batchId there would swap a working
// grid for the "unavailable" state instead of an actual improvement.
export function resolveRefreshTarget(
  latestBatch: LatestBatchCandidate | null | undefined,
  intradayPricingAsOf: string | null | undefined,
  fallback: { batchId: string | null, asOf: string | null, date?: string | null },
  options: { preferBatch?: boolean } = {},
): RefreshTarget {
  const targetsBatch = options.preferBatch
    ? Boolean(latestBatch?.id)
    : isBatchNewerThanPricing(latestBatch, intradayPricingAsOf)

  if (targetsBatch) {
    return {
      batchId: latestBatch?.id ?? fallback.batchId,
      asOf: latestBatch?.asOf ?? fallback.asOf,
      date: latestBatch?.date ?? fallback.date ?? null,
    }
  }
  return { batchId: null, asOf: intradayPricingAsOf ?? fallback.asOf, date: null }
}
